/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Paper from '@material-ui/core/Paper';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import {
  Alert, Card, CardFooter, Col, Container, Row, Spinner, Table
} from "reactstrap";
import CustomPagination from '../components/CustomPagination';
import Header from "../components/Headers/Header";
import { useAllCurrentExecutions } from '../hooks/useAllCurrentExecutions';
import { useQueryParams } from '../hooks/useQueryParams';
import { statusToProgress } from '../utils/statusToProgress';
import { toTitleCase } from '../utils/toTitleCase';
const RESULTS_PER_PAGE = 10;

const CurrentRuns: React.FC = () => {
  const query = useQueryParams()
  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })

  const {
    isError,
    isLoading,
    data,
    refetch,
    isStale
  } = useAllCurrentExecutions(pageTable, RESULTS_PER_PAGE)

  useEffect(() => {
    if (isStale) {
      refetch()
    }
  }, [pageTable, refetch, isStale])

  let content: JSX.Element | null = null

  if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    );
  } else if (isLoading) {
    content = <Spinner />
  } else if (!data || data.data.length === 0) {
    content = (
      <Alert color="warning">
        No data available
      </Alert>
    );
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Conference Name</th>
              <th scope="col">Session Name</th>
              <th scope="col">URL</th>
              <th scope="col">Executed By</th>
              <th scope="col">Status</th>
              <th scope="col">Started</th>
            </tr>
          </thead>
          <tbody>
            {
              data.data.map((ex, i) => (
                <tr key={i}>
                  <th scope="row">{ex.id}</th>
                  <th scope="row">{toTitleCase(ex.confName)}</th>
                  <th scope="row" title={toTitleCase(ex.sessionName)}>{toTitleCase(ex.sessionName).slice(0, 10)}{ex.sessionName.length > 10 && '...'}</th>
                  <td>
                    <Link to={`/admin/execution/${ex.id}`}>
                      <i className="fas fa-link mr-1" />
                    </Link>
                  </td>
                  <td>{toTitleCase(ex.user.name)}</td>
                  <td>
                    {!ex.status ? 'Queued' :
                      <>
                        {toTitleCase(ex.status)}&nbsp;
                        {statusToProgress(ex.status, ex.startingState, ex.steps) ? `(${statusToProgress(ex.status, ex.startingState, ex.steps)})` : ''}
                      </>
                    }
                  </td>
                  <td>{!ex.startedAt ? 'NA' : DateTime.fromJSDate(new Date(ex.startedAt)).toRelative()}</td>
                </tr>
              ))
            }
          </tbody>
        </Table>
        <CardFooter className="py-4">
          <nav aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    )
  }
  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <Paper className="px-4 py-4">
                <div className="mb-3">
                  <h4 style={{ display: 'inline' }}>Currently Runing Executions</h4>
                </div>
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CurrentRuns;
